import { useState } from "react";
import { ASSETS } from "@/lib/assets";
import { ArrowRight, Leaf } from "lucide-react";
import { toast } from "sonner";

export default function ContactCTA() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      toast.success("Welcome to the inner circle. Watch your inbox.");
      setEmail("");
      setSubmitting(false);
    }, 600);
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0A0A0A] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 lg:py-40 grid grid-cols-12 gap-8 md:gap-12 lg:gap-20 items-center">
        {/* Floral image column */}
        <div className="col-span-12 lg:col-span-6 reveal order-2 lg:order-1">
          <div className="relative">
            {/* Green outer frame */}
            <div className="absolute -inset-3 border border-[#6B6B6B]/40 hidden md:block" />
            {/* Soft sage backdrop bleed */}
            <div className="absolute -top-8 -left-8 w-40 h-40 bg-[#D9DDC7]/50 rounded-full blur-3xl hidden md:block" />
            <div className="zoom-frame relative bg-[#151618]">
              <img
                src={ASSETS.floralTexture}
                alt="Floral arrangement — lilies, roses & eucalyptus"
                data-testid="contact-floral-image"
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
            <div className="mt-5 flex items-center justify-between text-[10px] tracking-[0.28em] uppercase text-[#A0A0A0]">
              <span className="flex items-center gap-2">
                <Leaf size={12} className="text-[#6B6B6B]" />
                In Bloom
              </span>
              <span className="font-serif italic text-sm text-[#EDEDED] normal-case tracking-normal">
                — from the founder’s table
              </span>
            </div>
          </div>
        </div>

        {/* Copy column */}
        <div className="col-span-12 lg:col-span-6 reveal order-1 lg:order-2">
          <div className="text-[11px] tracking-[0.32em] uppercase text-[#C9A66B] mb-4 flex items-center gap-2">
            <Leaf size={14} className="text-[#6B6B6B]" />
            Insights &amp; Updates
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-[#EDEDED]">
            A briefing, <em>occasionally</em>. <br />
            Always worth opening.
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#A0A0A0] font-light max-w-xl">
            Strategic notes on procurement, workforce development, and
            operational excellence — delivered when we have something
            meaningful to share with the leaders we serve.
          </p>

          <form
            onSubmit={onSubmit}
            data-testid="newsletter-form"
            className="mt-10 max-w-xl"
          >
            <div className="flex items-center border-b border-[#EDEDED] focus-within:border-[#6B6B6B] transition-colors duration-300">
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email-input"
                className="flex-1 bg-transparent py-4 px-1 text-[#EDEDED] placeholder-[#5E5C5A]/60 outline-none text-base md:text-lg font-light tracking-wide"
                aria-label="Email address"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="newsletter-submit-button"
                className="ml-3 p-3 text-[#EDEDED] hover:text-[#6B6B6B] transition-colors duration-300 disabled:opacity-50 cta-arrow"
                aria-label="Subscribe"
              >
                <span className="text-[10px] tracking-[0.3em] uppercase hidden sm:inline">
                  {submitting ? "Sending" : "Join"}
                </span>
                <ArrowRight size={18} />
              </button>
            </div>
            <p className="mt-4 text-[10px] tracking-[0.24em] uppercase text-[#A0A0A0]">
              By subscribing you agree to receive correspondence from Becoming
              H.E.R. LLC.
            </p>
          </form>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-xl">
            <div className="border-t border-[#EDEDED]/20 pt-4 sm:col-span-2">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#A0A0A0]">
                Direct Contact
              </div>
              <a
                href="mailto:t.t.becomingh.e.r@gmail.com"
                data-testid="contact-link-email"
                className="font-serif italic text-lg md:text-xl text-[#EDEDED] hover-underline break-all"
              >
                t.t.becomingh.e.r@gmail.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

